import { useEffect, useState } from 'react'
import { BRANDS, BRAND_INFO } from '@hub/shared'
import type { BrandId } from '@hub/shared'
import { BrandTag } from '../components/Brand.js'
import { Icon } from '../components/Icon.js'
import { PageHeader } from '../components/PageHeader.js'
import { ApiError } from '../lib/api.js'
import { addDays, relative, short, today } from '../lib/dates.js'
import { useFilters, useSheetParam } from '../lib/filters.js'
import { SOURCE_LABEL, useDeleteInspiration, useInspiration, useSaveLink, useSetInspiration } from '../lib/inspiration.js'
import type { Inspiration as Find, InspirationStatus } from '../lib/inspiration.js'

/** No "Verworfen" tab any more: what is not wanted gets deleted (Lucas, 19.10.2026). */
const TABS: { id: InspirationStatus; label: string }[] = [
  { id: 'neu', label: 'Neu' },
  { id: 'gemerkt', label: 'Gemerkt' },
  { id: 'umgesetzt', label: 'Umgesetzt' },
]

/** Lead time back from the occasion, read from the summary the server wrote. */
function releaseDay(find: Find): string | undefined {
  if (!find.date) return undefined
  const lead = Number(find.summary.match(/etwa (\d+) Tage vorher/)?.[1] ?? 7)
  const day = addDays(find.date, -lead)
  return day < today() ? today() : day
}

function Score({ value }: { value: number }): JSX.Element {
  const tone = value >= 75 ? '#34C759' : value >= 50 ? '#FF9500' : '#8E8E93'
  return (
    <span className="num inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-caption font-semibold" style={{ color: tone, backgroundColor: `${tone}1F` }} title="Passung zur Marke">
      {value}
    </span>
  )
}

/** Two taps: the first asks, the second deletes. No dialog for something this small. */
function DeleteButton({ id }: { id: string }): JSX.Element {
  const remove = useDeleteInspiration()
  const [armed, setArmed] = useState(false)
  useEffect(() => {
    if (!armed) return
    const t = setTimeout(() => setArmed(false), 3000)
    return () => clearTimeout(t)
  }, [armed])
  return armed ? (
    <button type="button" onClick={() => remove.mutate(id)} className="min-h-[34px] shrink-0 rounded-full bg-danger/10 px-3 text-small font-semibold text-danger">
      Löschen?
    </button>
  ) : (
    <button
      type="button"
      aria-label="Löschen"
      onClick={() => setArmed(true)}
      className="grid h-[34px] w-[34px] shrink-0 place-items-center rounded-full text-label2/50 hover:bg-fill/[0.1] hover:text-danger"
    >
      <Icon name="trash" size={17} />
    </button>
  )
}

function Actions({ find }: { find: Find }): JSX.Element {
  const set = useSetInspiration()
  const { openFromIdea } = useSheetParam()
  if (find.status === 'umgesetzt') {
    return (
      <div className="flex items-center justify-between gap-1">
        <span className="text-caption text-success">Im Plan</span>
        <DeleteButton id={find.id} />
      </div>
    )
  }
  return (
    <div className="flex flex-wrap items-center gap-1">
      <button type="button" onClick={() => openFromIdea(find.id, releaseDay(find))} className="btn-ghost min-h-[34px] px-3 text-small">
        In den Plan
      </button>
      {find.status !== 'gemerkt' && (
        <button type="button" onClick={() => set.mutate({ id: find.id, status: 'gemerkt' })} className="min-h-[34px] rounded-full px-3 text-small text-label2/70 hover:bg-fill/[0.1]">
          Merken
        </button>
      )}
      <span className="ml-auto">
        <DeleteButton id={find.id} />
      </span>
    </div>
  )
}

function FindCard({ find }: { find: Find }): JSX.Element {
  return (
    <article className="flex min-w-0 flex-col overflow-hidden rounded-card bg-cell">
      {find.imageUrl && (
        <a href={find.url ?? undefined} target="_blank" rel="noreferrer noopener" className="block aspect-[4/3] overflow-hidden bg-fill/[0.1]">
          <img src={find.imageUrl} alt="" loading="lazy" referrerPolicy="no-referrer" className="h-full w-full object-cover" />
        </a>
      )}
      <div className="flex flex-1 flex-col p-4">
        <div className="flex flex-wrap items-center gap-1.5">
          {find.brand ? <BrandTag brand={find.brand} /> : <span className="text-caption text-label2/50">alle Marken</span>}
          {find.score !== null && <Score value={find.score} />}
          <span className="text-caption text-label2/50">{SOURCE_LABEL[find.source]}</span>
        </div>
        <h3 className="mt-2 break-words text-callout font-semibold leading-snug">
          {find.url ? (
            <a href={find.url} target="_blank" rel="noreferrer noopener" className="hover:text-pebro">
              {find.title}
            </a>
          ) : (
            find.title
          )}
        </h3>
        {find.summary && <p className="mt-1 line-clamp-4 whitespace-pre-line text-small text-label2/70">{find.summary}</p>}
        {find.tags.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {find.tags.map((t) => (
              <span key={t} className="rounded-full bg-fill/[0.1] px-2 py-0.5 text-caption text-label2/70">
                #{t}
              </span>
            ))}
          </div>
        )}
        <div className="mt-auto pt-3">
          <Actions find={find} />
        </div>
      </div>
    </article>
  )
}

function SaveLink(): JSX.Element {
  const [url, setUrl] = useState('')
  const [brand, setBrand] = useState<BrandId | null>(null)
  const save = useSaveLink()
  return (
    <form
      className="card flex flex-col gap-3 sm:flex-row sm:items-center"
      onSubmit={(e) => {
        e.preventDefault()
        save.mutate({ url: url.trim(), brand }, { onSuccess: () => setUrl('') })
      }}
    >
      <input
        className="field min-h-[44px] flex-1"
        type="url"
        inputMode="url"
        placeholder="Link merken – Pinterest, Instagram, Wettbewerber, Artikel …"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
      />
      <div className="flex items-center gap-2">
        <select className="field min-h-[44px] w-auto text-small" value={brand ?? ''} onChange={(e) => setBrand((e.target.value || null) as BrandId | null)} aria-label="Marke">
          <option value="">alle Marken</option>
          {BRANDS.map((b) => (
            <option key={b} value={b}>
              {BRAND_INFO[b].name}
            </option>
          ))}
        </select>
        <button type="submit" className="btn-primary min-h-[44px]" disabled={!/^https:\/\//.test(url.trim()) || save.isPending}>
          {save.isPending ? 'Lese …' : 'Merken'}
        </button>
      </div>
      {save.error && <p className="text-small text-danger">{save.error instanceof ApiError ? save.error.message : 'Konnte nicht gespeichert werden.'}</p>}
    </form>
  )
}

/** Ideas from occasions, saved links and Claude's research — one place to pick what to make next. */
export function Inspiration(): JSX.Element {
  const [tab, setTab] = useState<InspirationStatus>('neu')
  const { filters } = useFilters()
  const { data = [], isLoading } = useInspiration([tab], filters.brand)
  const { openFromIdea } = useSheetParam()

  const occasions = data.filter((x) => x.source === 'anlass')
  const finds = data.filter((x) => x.source !== 'anlass')

  return (
    <div className="mx-auto max-w-6xl space-y-6 px-5 py-6 md:px-8">
      <PageHeader title="Inspiration" subtitle="Anlässe, gemerkte Links und Recherche von Claude" />
      <SaveLink />

      <div className="flex items-center gap-1 overflow-x-auto rounded-full bg-cell p-1 [scrollbar-width:none] sm:w-fit">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`h-8 shrink-0 rounded-full px-4 text-small transition-colors ${tab === t.id ? 'bg-label font-medium text-cell' : 'text-label2/70 hover:text-label'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {occasions.length > 0 && (
        <section>
          <p className="section-title px-0">Anlässe</p>
          <div className="inset-group">
            {occasions.map((o) => {
              const release = releaseDay(o)
              return (
                <div key={o.id} className="flex items-center gap-4 px-4 py-3">
                  <div className="w-14 shrink-0 text-center">
                    <p className="num font-brand text-h3 font-light leading-none">{o.date?.slice(8, 10)}</p>
                    <p className="text-caption text-label2/60">{o.date ? short(o.date).split(' ')[0] : ''}</p>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="break-words text-callout font-semibold">{o.title}</p>
                    <p className="line-clamp-2 text-small text-label2/65">{o.summary}</p>
                    {release && <p className="mt-0.5 text-caption text-pebro">Ausspielen ab {relative(release)}</p>}
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    {o.status === 'umgesetzt' ? (
                      <span className="text-caption text-success">Im Plan</span>
                    ) : (
                      <button type="button" onClick={() => openFromIdea(o.id, release)} className="btn-ghost min-h-[34px] px-3 text-small">
                        Planen
                      </button>
                    )}
                    <DeleteButton id={o.id} />
                  </div>
                </div>
              )
            })}
          </div>
        </section>
      )}

      {finds.length > 0 && (
        <section>
          <p className="section-title px-0">Fundstücke</p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {finds.map((f) => (
              <FindCard key={f.id} find={f} />
            ))}
          </div>
        </section>
      )}

      {!isLoading && data.length === 0 && (
        <div className="card text-center">
          <p className="text-body">{tab === 'neu' ? 'Gerade nichts Neues.' : 'Hier ist nichts.'}</p>
          <p className="mx-auto mt-1 max-w-md text-small text-label2/60">
            Anlässe kommen automatisch. Links merkst du oben, und Claude legt über den Konnektor Recherche-Ergebnisse hier ab.
          </p>
        </div>
      )}
    </div>
  )
}
